import React, { useEffect, useState } from 'react';
import { Heart, Brain, Sun, Moon, Wind, BookOpen, Loader2, Check, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { Card } from './shared';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ShojinPractice from './ShojinPractice';
import WellbeingWeekStrip from './WellbeingWeekStrip';

/**
 * Mental Wellbeing module — sits under Training → Recovery alongside the
 * existing physio recovery card. Provides:
 *   • Daily check-in (mood / stress / sleep)
 *   • 7-day trend strip
 *   • Personalised practice recommendations
 *   • Full library of curated practices (breathwork, meditation, journaling, etc.)
 */

const CATEGORY_ICONS = {
  breathwork: Wind,
  meditation: Brain,
  journaling: BookOpen,
  mindset:    Sparkles,
  resilience: Sun,
  movement:   Heart,
  sleep:      Moon,
};

const Slider = ({ label, value, onChange, min = 1, max = 10, testid }) => (
  <div>
    <div className="flex items-baseline justify-between">
      <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">{label}</p>
      <span className="text-white text-sm font-black">{value}</span>
    </div>
    <input
      type="range" min={min} max={max} step="1" value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="w-full h-1.5 bg-zinc-800 rounded-full appearance-none accent-[#BEFF38] mt-1.5"
    />
  </div>
);

const CheckinCard = ({ today, onSaved }) => {
  const { toast } = useToast();
  const [mood, setMood] = useState(today?.mood ?? 7);
  const [stress, setStress] = useState(today?.stress ?? 4);
  const [sleepHours, setSleepHours] = useState(today?.sleep_hours ?? 7);
  const [sleepQuality, setSleepQuality] = useState(today?.sleep_quality ?? 7);
  const [notes, setNotes] = useState(today?.notes ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.wellbeingCheckin({
        mood, stress, sleep_hours: sleepHours, sleep_quality: sleepQuality, notes,
      });
      toast({ title: 'Check-in saved', description: 'Recommendations updated.' });
      onSaved && onSaved();
    } catch (e) {
      toast({ title: 'Could not save', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Card title="Today's check-in" icon={Heart}>
      <div className="grid grid-cols-2 gap-4">
        <Slider label="Mood" value={mood} onChange={setMood} testid="wellbeing-mood" />
        <Slider label="Stress" value={stress} onChange={setStress} testid="wellbeing-stress" />
        <Slider label="Sleep hours" value={sleepHours} onChange={setSleepHours} min={0} max={12} testid="wellbeing-sleep-hours" />
        <Slider label="Sleep quality" value={sleepQuality} onChange={setSleepQuality} testid="wellbeing-sleep-quality" />
      </div>
      <textarea
        rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder="Anything on your mind? (optional)"
        data-testid="wellbeing-notes"
        className="mt-3 w-full bg-[#0b0c10] border border-[#2a2f3a] focus:border-[#BEFF38] rounded-lg px-3 py-2 text-white text-sm outline-none resize-none"
      />
      <button
        onClick={save}
        disabled={busy}
        data-testid="wellbeing-save-checkin"
        className="mt-3 w-full h-11 rounded-full bg-[#BEFF38] text-black font-black uppercase tracking-wider text-sm hover:bg-[#a6e62e] disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={3} />}
        {today?.date ? 'Update check-in' : 'Save check-in'}
      </button>
    </Card>
  );
};

const TrendStrip = ({ checkins }) => {
  if (!checkins || checkins.length === 0) return null;
  const sorted = [...checkins].slice().reverse(); // oldest → newest
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
      <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Last {sorted.length} check-ins</p>
      <div className="mt-2 grid grid-cols-3 gap-3 text-center">
        <Metric label="Mood"      value={avg(sorted, 'mood')}      colour="text-[#BEFF38]" />
        <Metric label="Stress"    value={avg(sorted, 'stress')}    colour="text-amber-400" />
        <Metric label="Sleep h"   value={avg(sorted, 'sleep_hours')} colour="text-sky-400" />
      </div>
    </div>
  );
};

const Metric = ({ label, value, colour }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{label}</p>
    <p className={`text-2xl font-black ${colour} mt-0.5`}>{value}</p>
  </div>
);

const avg = (arr, key) => {
  const xs = arr.map((x) => Number(x[key] || 0));
  if (xs.length === 0) return '—';
  return (xs.reduce((a, b) => a + b, 0) / xs.length).toFixed(1);
};

const PracticeRow = ({ p, expanded, onToggle }) => {
  const Icon = CATEGORY_ICONS[p.category] || Brain;
  return (
    <div
      data-testid={`wellbeing-practice-${p.id}`}
      className="rounded-lg border border-zinc-800 bg-[#0b0c10]"
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-3 flex items-center gap-3"
      >
        <div className="shrink-0 w-9 h-9 rounded-lg bg-zinc-900 grid place-items-center">
          <Icon className="w-4 h-4 text-[#BEFF38]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white font-bold text-sm leading-tight">{p.name}</p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
            {p.category} · {p.minutes} min · {p.best_for.join(' / ')}
          </p>
        </div>
        {expanded
          ? <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
      </button>
      {expanded && (
        <ol className="px-4 pb-3 list-decimal list-inside space-y-1 text-zinc-300 text-[12px] leading-relaxed">
          {p.steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      )}
    </div>
  );
};

const MentalWellbeing = () => {
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [rec, setRec] = useState(null);
  const [practices, setPractices] = useState([]);
  const [openId, setOpenId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [t, all, r, lib] = await Promise.all([
        api.wellbeingToday(),
        api.wellbeingCheckins(14),
        api.wellbeingRecommend(),
        api.wellbeingPractices(),
      ]);
      setToday(t && t.date ? t : null);
      setCheckins(all || []);
      setRec(r || null);
      setPractices(lib || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-400 text-sm py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading wellbeing…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* This week — rolling averages + sparklines + deltas */}
      <WellbeingWeekStrip />

      {/* Shōjin Dō hero — the philosophical frame this whole tab sits inside */}
      <div
        data-testid="wellbeing-shojin-frame"
        className="rounded-2xl overflow-hidden border border-[#BEFF38]/15 bg-[#f7f4ee]"
      >
        <div className="relative">
          <img
            src="/shojin/shojin-do.png"
            alt="Shōjin Dō — the way of devoted, continuous effort"
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
        <div className="p-4 bg-[#0b0b0c] border-t border-white/5">
          <p className="text-[10px] uppercase tracking-[0.2em] font-black text-[#BEFF38]">Shōjin Dō · 精進道</p>
          <p className="mt-1.5 text-white text-base font-bold leading-snug">Practise the Way.</p>
          <p className="mt-1 text-zinc-300 text-xs leading-relaxed italic">
            Through disciplined practice, I walk my path.
          </p>
          <p className="mt-3 text-zinc-400 text-xs leading-relaxed">
            Bamboo bends but doesn't break — quiet resilience, continuous growth.
            The ensō closes what discipline opens.
            Consistency isn't a personality trait — it's a trained skill. Everything below (the daily check-in, the trend, the practices) exists to build the muscle that keeps you walking the path regardless of mood, energy or life.
          </p>
        </div>
      </div>

      <ShojinPractice />

      <CheckinCard today={today} onSaved={refresh} />
      {checkins.length > 0 && <TrendStrip checkins={checkins} />}

      {rec && rec.practices && rec.practices.length > 0 && (
        <Card title="Today's recommendations" icon={Sparkles}>
          {rec.reason && <p className="text-zinc-400 text-xs leading-relaxed">{rec.reason}</p>}
          <div className="mt-3 space-y-2">
            {rec.practices.map((p) => (
              <PracticeRow
                key={`rec-${p.id}`}
                p={p}
                expanded={openId === `rec-${p.id}`}
                onToggle={() => setOpenId(openId === `rec-${p.id}` ? null : `rec-${p.id}`)}
              />
            ))}
          </div>
        </Card>
      )}

      <Card title="Practice library" icon={Brain}>
        <p className="text-zinc-400 text-xs leading-relaxed">
          Breathwork, meditation, journaling and resilience drills. Tap one to open the steps.
        </p>
        <div className="mt-3 space-y-2">
          {practices.map((p) => (
            <PracticeRow
              key={p.id}
              p={p}
              expanded={openId === p.id}
              onToggle={() => setOpenId(openId === p.id ? null : p.id)}
            />
          ))}
        </div>
      </Card>
    </div>
  );
};

export default MentalWellbeing;
